import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { PRIVACY, TERMS } from '../../../router/routes';
import AuthButton from '../../../components/external/auth-button';
import warehouseImage from './warehouse.svg';
import cropImage from './crop.svg';
import livestockImage from './livestock.svg';
import { UserData } from './index';
import authCss from '../auth-forms.module.css';
import css from './index.module.css';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { parseError } from '../../../services/utils';

const sectors = {
  warehouse: true,
  crop: false,
  livestock: false,
};

export default function Step3({
  userData,
  onSubmit,
}: {
  userData: UserData;
  onSubmit: (data: UserData) => void;
}) {
  const { t } = useTranslation();
  const [sectorsError, setSectorsError] = useState(false);
  const [schema] = useState(() =>
    yup
      .object()
      .shape({
        sectors: yup.object().shape({
          warehouse: yup.boolean(),
          crop: yup.boolean(),
          livestock: yup.boolean(),
        }),
        agree: yup
          .boolean()
          .required()
          .oneOf(
            [true],
            'Lai reģistrētos, ir jāpiekrīt lietošanas noteikumiem un jāiepazīstas ar privātuma politiku!'
          ),
        agreeMarketing: yup.boolean(),
      })
      .test({
        name: 'warehouseOrcropOrLivestockRequired',
        test: function (values) {
          const isValid = ['warehouse', 'crop', 'livestock'].some(
            (field) => values.sectors[field]
          );

          if (isValid) {
            setSectorsError(false);
            return true;
          }

          setSectorsError(true);

          return this.createError({
            message: 'Crop or livestock required!',
          });
        },
      })
  );
  const {
    register,
    handleSubmit,
    formState,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...userData, sectors },
    resolver: yupResolver(schema),
  });
  const { isValid } = formState;

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <p className={css['step3-description']}>
        {t('pages.external.register.step3.sectors')}
      </p>

      {sectorsError && (
        <span className={authCss['error']}>
          {t('Crop or livestock required!')}
        </span>
      )}

      <Controller
        name="sectors.warehouse"
        control={control}
        defaultValue={userData.sectors.warehouse}
        render={({ field }) => (
          <div
            className={`${field.value ? css['active'] : ''} ${
              css['warehouse']
            }`}
          >
            <img src={warehouseImage} alt="" className="mb-3" />
            <span>{t('pages.external.register.step3.warehouse')}</span>
            <input
              name="warehouse"
              type="checkbox"
              className={css['checkbox']}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={true}
            />
          </div>
        )}
      />
      <Controller
        name="sectors.crop"
        control={control}
        defaultValue={userData.sectors.crop}
        render={({ field }) => (
          <div
            className={`${field.value ? css['active'] : ''} ${css['crop']}`}
            onClick={(e) => field.onChange(!field.value)}
          >
            <img src={cropImage} alt="" className="mb-3" />
            <span>{t('pages.external.register.step3.crop')}</span>
            <input
              name="crop"
              type="checkbox"
              className={css['checkbox']}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          </div>
        )}
      />
      <Controller
        name="sectors.livestock"
        control={control}
        defaultValue={userData.sectors.livestock}
        render={({ field, formState }) => (
          <div
            className={`${field.value ? css['active'] : ''} ${
              css['livestock']
            }`}
            onClick={(e) => field.onChange(!field.value)}
          >
            <img src={livestockImage} alt="" className="mb-3" />
            <span>{t('pages.external.register.step3.livestock')}</span>
            <input
              name="livestock"
              type="checkbox"
              className={css['checkbox']}
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          </div>
        )}
      />

      <div className={'mb-9 ' + authCss['checkbox-field']}>
        <input
          type="checkbox"
          id="agree"
          name="agree"
          className={authCss['checkbox']}
          {...register('agree')}
        />
        <label htmlFor="agree">
          <Trans
            i18nKey="pages.external.register.step3.link"
            components={{
              1: <Link to={PRIVACY} target="_blank" />,
              2: <Link to={TERMS} target="_blank" />,
            }}
          />
        </label>
      </div>

      <div className={'mb-9 ' + authCss['checkbox-field']}>
        <input
          type="checkbox"
          id="agreeMarketing"
          name="agreeMarketing"
          className={authCss['checkbox']}
          {...register('agreeMarketing')}
        />
        <label htmlFor="agreeMarketing">
          <Trans
            i18nKey="pages.external.register.step3.agreeMarketing"
            components={{
              1: <Link to={PRIVACY} target="_blank" />,
              2: <Link to={TERMS} target="_blank" />,
            }}
          />
        </label>
      </div>

      {errors.agree && (
        <span className={authCss['error']}>
          {t(parseError('agree', errors)?.message)}
        </span>
      )}

      <div className={authCss['buttons']}>
        <AuthButton
          name="register"
          label={t('pages.external.register.register')}
          type="submit"
          disabled={!isValid}
        />
      </div>
    </form>
  );
}
